import { useEffect, useState } from 'react';
import { Shield, Users, Activity, Database, TrendingUp, Send, Bell, RefreshCw, Edit } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { StatCard } from '@/components/Dashboard/StatCard';
import { SEO } from '@/components/SEO';
import { AgentJobsViewer } from '@/components/Admin/AgentJobsViewer';
import { SystemHealthDashboard } from '@/components/Admin/SystemHealthDashboard';

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Admin verification state - prevents UI flashing
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    proUsers: 0,
    totalInvoices: 0,
    totalTransactions: 0
  });
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    type: 'info',
    targetUserId: 'all'
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const checkAdminRole = async () => {
      try {
        const { data: roleData, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .single();

        if (error || !roleData || roleData.role !== 'admin') {
          setIsAdmin(false);
          navigate('/dashboard');
          return;
        }

        // User is confirmed admin - now safe to render admin UI
        setIsAdmin(true);
        fetchAdminData();
        setupRealtimeSubscription();
      } catch (error) {
        console.error('Admin check error:', error);
        setIsAdmin(false);
        navigate('/dashboard');
      }
    };

    checkAdminRole();
  }, [user, navigate]);

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel('admin-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => {
        fetchAdminData();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'invoices' }, () => {
        fetchAdminData();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bank_transactions' }, () => {
        fetchAdminData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const fetchAdminData = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase.rpc('get_admin_stats');

      if (error) {
        console.error('Error fetching admin data:', error);
        navigate('/dashboard');
        return;
      }

      const adminData = data as {
        totalUsers: number;
        proUsers: number;
        totalInvoices: number;
        totalTransactions: number;
        users: any[];
      };

      setStats({
        totalUsers: adminData.totalUsers || 0,
        proUsers: adminData.proUsers || 0,
        totalInvoices: adminData.totalInvoices || 0,
        totalTransactions: adminData.totalTransactions || 0
      });
      setUsers(adminData.users || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendNotification = async () => {
    try {
      const targetUsers = notificationForm.targetUserId === 'all' 
        ? users 
        : users.filter(u => u.id === notificationForm.targetUserId);

      const results = await Promise.all(
        targetUsers.map(async (targetUser) => {
          const { data, error } = await supabase.functions.invoke('send-notification', {
            body: {
              title: notificationForm.title,
              message: notificationForm.message,
              type: notificationForm.type,
              targetUserId: targetUser.id
            }
          });
          return { userId: targetUser.id, success: !error, error };
        })
      );

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      if (failCount === 0) {
        toast({
          title: "Notifications Sent",
          description: `Successfully sent to ${successCount} user(s).`,
        });
      } else if (successCount > 0) {
        toast({
          title: "Partial Success",
          description: `Sent to ${successCount} user(s), ${failCount} failed.`,
        });
      } else {
        throw new Error('All notifications failed');
      }

      setNotificationForm({ title: '', message: '', type: 'info', targetUserId: 'all' });
    } catch (error) {
      console.error('Error sending notification:', error);
      toast({
        variant: "destructive",
        title: "Failed to Send",
        description: "Could not send notification.",
      });
    }
  };

  const updateUserSubscription = async (userId: string, newSubscription: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ subscription: newSubscription })
        .eq('id', userId);

      if (error) throw error;

      toast({
        title: "Subscription Updated",
        description: "User subscription changed successfully.",
      });
      
      fetchAdminData();
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: "Could not update subscription.",
      });
    }
  };

  // Show loading while checking admin status - prevents UI flash
  if (isAdmin === null || loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Extra safety: don't render if not admin
  if (!isAdmin) {
    return null;
  }

  return (
    <>
      <SEO
        title="Admin Panel - System Management | Sentinel"
        description="Admin dashboard for managing users, notifications, and system operations."
        noindex={true}
      />
      
      <div className="p-4 md:p-8 h-full overflow-y-auto">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
              <Shield className="text-primary" />
              Admin Panel
            </h1>
            <p className="text-muted-foreground mt-2">
              System-wide management and monitoring
            </p>
          </div>
          <Button onClick={fetchAdminData} variant="outline" className="rounded-2xl">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Data
          </Button>
        </div>

        {/* Real-time Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <StatCard
            title="Total Users"
            value={stats.totalUsers.toString()}
            icon={Users}
            colorClass="bg-blue-500/10 text-blue-500"
          />
          <StatCard
            title="Pro Subscribers"
            value={stats.proUsers.toString()}
            icon={TrendingUp}
            colorClass="bg-success/10 text-success"
          />
          <StatCard
            title="Total Invoices"
            value={stats.totalInvoices.toString()}
            icon={Activity}
            colorClass="bg-purple-500/10 text-purple-500"
          />
          <StatCard
            title="Transactions"
            value={stats.totalTransactions.toString()}
            icon={Database}
            colorClass="bg-primary/10 text-primary"
          />
        </div>

        {/* Tabs for different admin functions */}
        <Tabs defaultValue="system" className="space-y-6">
          <TabsList className="rounded-2xl p-1 h-auto flex-wrap">
            <TabsTrigger value="system" className="rounded-xl py-2">System Health</TabsTrigger>
            <TabsTrigger value="jobs" className="rounded-xl py-2">Agent Jobs</TabsTrigger>
            <TabsTrigger value="users" className="rounded-xl py-2">Users</TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-xl py-2">Notifications</TabsTrigger>
          </TabsList>

          {/* System Health Tab */}
          <TabsContent value="system" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SystemHealthDashboard />
              <AgentJobsViewer />
            </div>
          </TabsContent>

          {/* Agent Jobs Tab */}
          <TabsContent value="jobs" className="space-y-4">
            <AgentJobsViewer />
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="users" className="space-y-4">
            <Card className="border-border overflow-hidden rounded-3xl">
              <div className="p-4 border-b border-border bg-surface">
                <h2 className="font-bold text-foreground">All Users</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface border-b border-border">
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <th className="p-4">Name</th>
                      <th className="p-4">Email</th>
                      <th className="p-4">Subscription</th>
                      <th className="p-4">Joined</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {users.map((userData) => (
                      <tr key={userData.id} className="hover:bg-surface/50">
                        <td className="p-4 text-foreground font-medium">{userData.name}</td>
                        <td className="p-4 text-muted-foreground">{userData.email}</td>
                        <td className="p-4">
                          <Badge variant={
                            userData.subscription === 'business' ? 'default' :
                            userData.subscription === 'pro' ? 'default' :
                            userData.subscription === 'starter' ? 'secondary' : 'outline'
                          } className="rounded-xl">
                            {userData.subscription}
                          </Badge>
                        </td>
                        <td className="p-4 text-muted-foreground text-sm">
                          {new Date(userData.joined_at).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" onClick={() => setSelectedUser(userData)} className="rounded-xl">
                                <Edit className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-3xl">
                              <DialogHeader>
                                <DialogTitle>Manage User: {userData.name}</DialogTitle>
                                <DialogDescription>
                                  Update user subscription and settings
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div>
                                  <Label>Email</Label>
                                  <Input value={userData.email} disabled className="rounded-xl" />
                                </div>
                                <div>
                                  <Label>Subscription</Label>
                                  <Select 
                                    defaultValue={userData.subscription}
                                    onValueChange={(value) => updateUserSubscription(userData.id, value)}
                                  >
                                    <SelectTrigger className="rounded-xl">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="free">Free</SelectItem>
                                      <SelectItem value="starter">Starter</SelectItem>
                                      <SelectItem value="pro">Pro</SelectItem>
                                      <SelectItem value="business">Business</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-4">
            <Card className="p-6 rounded-3xl">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-primary" />
                    Broadcast Notification
                  </h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="target">Send To</Label>
                    <Select 
                      value={notificationForm.targetUserId}
                      onValueChange={(value) => setNotificationForm({ ...notificationForm, targetUserId: value })}
                    >
                      <SelectTrigger id="target" className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name} ({u.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="notif-type">Type</Label>
                    <Select 
                      value={notificationForm.type}
                      onValueChange={(value) => setNotificationForm({ ...notificationForm, type: value })}
                    >
                      <SelectTrigger id="notif-type" className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="info">Info</SelectItem>
                        <SelectItem value="success">Success</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="error">Error</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={notificationForm.title}
                      onChange={(e) => setNotificationForm({ ...notificationForm, title: e.target.value })}
                      placeholder="Notification title"
                      className="rounded-xl"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      value={notificationForm.message}
                      onChange={(e) => setNotificationForm({ ...notificationForm, message: e.target.value })}
                      placeholder="Notification message"
                      rows={4}
                      className="rounded-xl"
                    />
                  </div>

                  <Button onClick={sendNotification} className="w-full rounded-2xl">
                    <Send className="w-4 h-4 mr-2" />
                    Send Notification
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
